import dayjs from "dayjs";

import { CountryFormConfiguration, Gender } from "../constants";
import { defaultForbiddenWordsValidation } from "./defaults";
import { sanitizeStringValue } from "../../utils/replacement";
import { SanitizeMode } from "../next-gen-config-wip";
import { getAddressData } from "../../api/avs";

const getAddresses = async (areas: string[]) =>
  (await getAddressData("el-GR", areas)).values;

const getPostalCodes = async (): Promise<string[]> => await getAddresses([]);

export const Greece: CountryFormConfiguration = {
  id: "el-GR",
  forms: {
    personalInfo: {
      beenMemberBefore: {
        name: "beenMemberBefore",
        label: "PREVIOUS_MEMBERSHIP",
        labelExtraClassNames: ["enlargedLabelText"],
        type: "radio",
        options: [
          { value: true, label: "YES" },
          { value: false, label: "NO" },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
        classNames: ["centerJustified"],
        validationErrorStyling: ["textCentered"],
      },
      firstName: {
        name: "firstName",
        label: "FIRST_NAME",
        type: "text",
        sanitize: [
          { mode: SanitizeMode.ON_CHANGE, sanitizeFn: sanitizeStringValue },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "FIRST_NAME_SIZE",
          },
          maxLength: {
            value: 20,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: /^[a-zA-Z\u0370-\u03FF\u1F00-\u1FFF \-‘]+$/u,
            errorMessage: "FIRST_NAME_SIZE",
          },
          trim: true,
        },
      },
      lastName: {
        name: "lastName",
        label: "LAST_NAME",
        type: "text",
        sanitize: [
          { mode: SanitizeMode.ON_CHANGE, sanitizeFn: sanitizeStringValue },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "FIRST_NAME_SIZE",
          },
          maxLength: {
            value: 30,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: /^[a-zA-Z\u0370-\u03FF\u1F00-\u1FFF \-‘]+$/u,
            errorMessage: "FIRST_NAME_SIZE",
          },
          trim: true,
        },
      },
      gender: {
        name: "gender",
        label: "GENDER",
        type: "radio",
        options: [
          { value: Gender.MALE, label: "MALE" },
          { value: Gender.FEMALE, label: "FEMALE" },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
        validationErrorStyling: ["textCentered"],
      },
      dateOfBirth: {
        name: "dateOfBirth",
        label: "BIRTHDAY",
        type: "date",
        bottomDescription: { text: "BIRTHDAY_DISCLAIMER" },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxDate: {
            value: dayjs().subtract(18, "year"),
            errorMessage: "TOO_YOUNG",
          },
          minDate: {
            value: dayjs().subtract(120, "year"),
            errorMessage: "TOO_OLD",
          },
        },
      },
      addressLine1: {
        name: "addressLine1",
        label: "ADDRESS",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 150,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: /^[\u0370-\u03FF\u1F00-\u1FFF\s\-/‘\d]*$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      addressLine2: {
        name: "addressLine2",
        label: "ADDRESS_LINE_2",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 150,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: /^[\u0370-\u03FF\u1F00-\u1FFF\s\-,/‘’\d]*$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      city: {
        name: "city",
        label: "CITY",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 60,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: /^[\u0370-\u03FF\u1F00-\u1FFF \-/‘&]+$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          trim: true,
        },
      },
      province: {
        name: "province",
        label: "REGION",
        type: "text",
        validation: {
          required: { value: false },
          maxLength: {
            value: 40,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: /^[\u0370-\u03FF\u1F00-\u1FFF]+$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          trim: true,
        },
      },
      postalCode: {
        name: "postalCode",
        label: "POSTAL_CODE",
        type: "asyncAutocomplete",
        defaultValue: "",
        dependenciesNames: [],
        getOptions: getPostalCodes,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      dataPrivacy: {
        name: "dataPrivacy",
        header: "DATA_PRIVACY_HEADER",
        type: "static",
        staticValue: "DATA_PRIVACY_STATEMENT",
      },
      dataPrivacyDisclosure: {
        name: "dataPrivacyDisclosure",
        type: "static",
        staticValue: "INFORMATION_DISCLOSURE",
      },
      additionalComunicationConsent: {
        name: "additionalComunicationConsent",
        type: "static",
        staticValue: "ADDITIONAL_COMMUNICATION_CONSENT",
      },
      marketingConsentAccepted: {
        name: "marketingConsentAccepted",
        label: "EXTRA_COMMUNICATION_CONSENT",
        type: "checkbox",
        validation: {
          required: { value: false },
        },
      },
    },
    shippingAddress: {
      careOfName: {
        name: "careOfName",
        label: "HMP_CARE_OF_NAME",
        type: "text",
        sanitize: [
          { mode: SanitizeMode.ON_CHANGE, sanitizeFn: sanitizeStringValue },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 51,
            errorMessage: "HMP_CARE_OF_NAME_SIZE",
          },
          pattern: {
            value: /^[a-zA-Z\u0370-\u03FF\u1F00-\u1FFF \-‘]+$/u,
            errorMessage: "REQUIRED",
          },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "REQUIRED",
          },
          trim: true,
        },
      },
      addressLine1: {
        name: "addressLine1",
        label: "ADDRESS",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 150,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: /^[\u0370-\u03FF\u1F00-\u1FFF\s\-,/‘’\d]*$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      addressLine2: {
        name: "addressLine2",
        label: "ADDRESS_LINE_2",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 150,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: /^[\u0370-\u03FF\u1F00-\u1FFF\s\-,/‘’\d]*$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      city: {
        name: "city",
        label: "CITY",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 60,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: /^[\u0370-\u03FF\u1F00-\u1FFF -/‘&]+$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          trim: true,
        },
      },
      province: {
        name: "province",
        label: "REGION",
        type: "text",
        validation: {
          required: { value: false },
          maxLength: {
            value: 40,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: /^[\u0370-\u03FF\u1F00-\u1FFF ]+$/u,
            errorMessage: "ADDRESS_SIZE",
          },
          trim: true,
        },
      },
      postalCode: {
        name: "postalCode",
        label: "POSTAL_CODE",
        type: "asyncAutocomplete",
        defaultValue: "",
        dependenciesNames: [],
        getOptions: getPostalCodes,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
    },
  },
  footerLinks: ["PRIVACY", "COOKIE_POLICY", "RULES_OF_CONDUCT", "COPYRIGHT"],
  footerLogos: [],
  deliveryPriceKeys: ["retail", "deliveryTotal", "tax", "totalAmountToPay"],
  showHmpBenefits: true,
  showHmpPriceContent: true,
  showLinkToOrderConfirmation: true,
  showPdfContainer: true,
  supportedCountryCodes: ["gr"],
  defaultCountryCode: "+30|Greece",
  hasDifferentHmpCollection: true,
};
